// src/components/RunpodLoadActivity.jsx
//
// Load pattern data is based on observed GPU cloud usage patterns:
// - Peak hours correlate with US/EU business hours and evenings
// - Weekends are heavier during US daytime, lighter very early morning UTC
// - Values: 0 = lightest (best), 4 = heaviest (worst)
//
// Admin can update LOAD_PATTERN in Firestore config/runpod as { loadPattern: [...] }
// Each row = day (0=Sun … 6=Sat), each col = UTC hour (0–23)

import React, { useState, useEffect, useCallback } from 'react';

// ── Default load pattern (UTC hours 0–23, days 0=Sun … 6=Sat) ────────────────
//
// Runpod/ComfyUI load is CONSUMER/HOBBYIST driven, NOT business hours driven.
// Verified against real-world observation: Auckland worst = 3pm–midnight local,
// best = 2am–9am local (UTC+13 summer).
//
// Auckland worst  15:00–00:00 local = UTC 02:00–11:00  → must be HEAVY
// Auckland best   02:00–09:00 local = UTC 13:00–20:00  → must be LIGHT/QUIET
//
// UTC load breakdown:
//   UTC 00–01 = US 7–8pm ET consumer prime time                    → 4 Overloaded
//   UTC 02–04 = US 9–11pm ET peak hobbyist AI (heaviest globally)  → 4 Overloaded
//   UTC 05–07 = US midnight–2am winding + EU 6–8am starting        → 3 Busy
//   UTC 08–11 = EU morning ramp, US fully asleep                   → 3 Busy
//   UTC 12–13 = EU midday, US 8–9am just waking                    → 2 Moderate
//   UTC 14–17 = US 10am–1pm business day (low hobbyist), EU afternoon → 1 Light
//   UTC 18–20 = US 2–4pm business + EU evening slowly starting     → 2 Moderate
//   UTC 21–23 = EU prime evening + US 5–7pm consumer ramp          → 3–4 Busy/Overloaded
//
// Weekend: heavier during UTC 14–22 (US daytime hobbyists), lighter Mon–Thu UTC 12–17
//
// Values: 0=Quiet  1=Light  2=Moderate  3=Busy  4=Overloaded
const DEFAULT_PATTERN = [
  //         UTC: 0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15 16 17 18 19 20 21 22 23
  /* Sun */ [4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 1, 2, 2, 3, 3, 3, 3, 3, 4, 4, 4],
  /* Mon */ [4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 1, 1, 1, 1, 2, 2, 2, 3, 3, 4, 4],
  /* Tue */ [4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 1, 1, 1, 1, 2, 2, 2, 3, 3, 4, 4],
  /* Wed */ [4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 1, 1, 1, 1, 2, 2, 2, 3, 3, 4, 4],
  /* Thu */ [4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 1, 1, 1, 1, 2, 2, 2, 3, 3, 4, 4],
  /* Fri */ [4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 1, 2, 2, 3, 3, 3, 3, 3, 4, 4, 4],
  /* Sat */ [4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 2, 2, 2, 1, 2, 2, 3, 3, 3, 3, 3, 4, 4, 4],
];

const DAYS   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Load level metadata — vivid colours readable on dark navy background
const LEVELS = [
  { label: 'Quiet',      color: '#10b981', bg: '#0d5c3a', border: '#10b981', text: '#6ee7b7' },
  { label: 'Light',      color: '#84cc16', bg: '#2d5a00', border: '#84cc16', text: '#bef264' },
  { label: 'Moderate',   color: '#f59e0b', bg: '#6b3a00', border: '#f59e0b', text: '#fcd34d' },
  { label: 'Busy',       color: '#f97316', bg: '#7c2200', border: '#f97316', text: '#fdba74' },
  { label: 'Overloaded', color: '#ef4444', bg: '#7f1d1d', border: '#ef4444', text: '#fca5a5' },
];

// ── All IANA timezone options grouped by region ───────────────────────────────
const TZ_GROUPS = [
  { label: 'UTC', zones: [{ label: 'UTC', value: 'UTC' }] },
  { label: 'Americas', zones: [
    { label: 'Honolulu (UTC-10)', value: 'Pacific/Honolulu' },
    { label: 'Anchorage (UTC-9)', value: 'America/Anchorage' },
    { label: 'Los Angeles (UTC-8/7)', value: 'America/Los_Angeles' },
    { label: 'Denver (UTC-7/6)', value: 'America/Denver' },
    { label: 'Chicago (UTC-6/5)', value: 'America/Chicago' },
    { label: 'New York (UTC-5/4)', value: 'America/New_York' },
    { label: 'Toronto (UTC-5/4)', value: 'America/Toronto' },
    { label: 'São Paulo (UTC-3)', value: 'America/Sao_Paulo' },
    { label: 'Buenos Aires (UTC-3)', value: 'America/Argentina/Buenos_Aires' },
  ]},
  { label: 'Europe', zones: [
    { label: 'London (UTC+0/1)', value: 'Europe/London' },
    { label: 'Lisbon (UTC+0/1)', value: 'Europe/Lisbon' },
    { label: 'Paris / Berlin (UTC+1/2)', value: 'Europe/Paris' },
    { label: 'Amsterdam (UTC+1/2)', value: 'Europe/Amsterdam' },
    { label: 'Warsaw (UTC+1/2)', value: 'Europe/Warsaw' },
    { label: 'Helsinki (UTC+2/3)', value: 'Europe/Helsinki' },
    { label: 'Moscow (UTC+3)', value: 'Europe/Moscow' },
    { label: 'Istanbul (UTC+3)', value: 'Europe/Istanbul' },
  ]},
  { label: 'Africa & Middle East', zones: [
    { label: 'Cairo (UTC+2)', value: 'Africa/Cairo' },
    { label: 'Nairobi (UTC+3)', value: 'Africa/Nairobi' },
    { label: 'Dubai (UTC+4)', value: 'Asia/Dubai' },
    { label: 'Riyadh (UTC+3)', value: 'Asia/Riyadh' },
  ]},
  { label: 'Asia & Pacific', zones: [
    { label: 'Karachi (UTC+5)', value: 'Asia/Karachi' },
    { label: 'Mumbai (UTC+5:30)', value: 'Asia/Kolkata' },
    { label: 'Dhaka (UTC+6)', value: 'Asia/Dhaka' },
    { label: 'Bangkok (UTC+7)', value: 'Asia/Bangkok' },
    { label: 'Singapore (UTC+8)', value: 'Asia/Singapore' },
    { label: 'Hong Kong (UTC+8)', value: 'Asia/Hong_Kong' },
    { label: 'Shanghai / Beijing (UTC+8)', value: 'Asia/Shanghai' },
    { label: 'Tokyo (UTC+9)', value: 'Asia/Tokyo' },
    { label: 'Seoul (UTC+9)', value: 'Asia/Seoul' },
    { label: 'Sydney (UTC+10/11)', value: 'Australia/Sydney' },
    { label: 'Melbourne (UTC+10/11)', value: 'Australia/Melbourne' },
    { label: 'Auckland (UTC+12/13)', value: 'Pacific/Auckland' },
  ]},
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function getNow(tz) {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: 'numeric', minute: 'numeric', second: 'numeric',
    weekday: 'short', hour12: false,
  }).formatToParts(now);
  const get = t => parts.find(p => p.type === t)?.value;
  const dayIdx = DAYS.indexOf(get('weekday'));
  const hour   = parseInt(get('hour'), 10) % 24;
  const minute = parseInt(get('minute'), 10);
  const second = parseInt(get('second'), 10);
  return { dayIdx, hour, minute, second };
}

function getUTCOffset(tz) {
  const now = new Date();
  const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
  const local = new Date(now.toLocaleString('en-US', { timeZone: tz }));
  const diff = (local - utc) / 3600000;
  const sign = diff >= 0 ? '+' : '-';
  const abs = Math.abs(diff);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `UTC${sign}${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
}

// Convert local {dayIdx, hour} to UTC {dayIdx, hour}
function localToUTC(localDay, localHour, tz) {
  const now = new Date();
  const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
  const local = new Date(now.toLocaleString('en-US', { timeZone: tz }));
  const offsetHours = (local - utc) / 3600000;
  let utcHour = localHour - offsetHours;
  let utcDay  = localDay;
  if (utcHour < 0)  { utcHour += 24; utcDay = (utcDay + 6) % 7; }
  if (utcHour >= 24){ utcHour -= 24; utcDay = (utcDay + 1) % 7; }
  return { utcDay: Math.round(utcDay), utcHour: Math.round(utcHour) };
}

function getLoadAt(pattern, utcDay, utcHour) {
  return pattern[utcDay]?.[utcHour] ?? 2;
}

// Find best window (lowest load) in the next 24 local hours
function findNextBestWindow(pattern, localDay, localHour, tz) {
  let best = { load: 99, hoursAhead: 0 };
  for (let ahead = 1; ahead <= 24; ahead++) {
    const lh = (localHour + ahead) % 24;
    const ld = (localDay + Math.floor((localHour + ahead) / 24)) % 7;
    const { utcDay, utcHour } = localToUTC(ld, lh, tz);
    const load = getLoadAt(pattern, utcDay, utcHour);
    if (load < best.load) best = { load, hoursAhead: ahead, localHour: lh, localDay: ld };
  }
  return best;
}

// ── Cell color by load level ──────────────────────────────────────────────────
function cellStyle(load, isNow, isToday) {
  const l = LEVELS[load];
  if (isNow) return {
    background: l.color, color: '#fff',
    border: `2px solid #fff`,
    borderRadius: 4, fontWeight: 700,
  };
  // Today = full vivid colour, other days = same bg, slightly less opacity
  // No double-dimming — bg is already vivid enough to show at 0.5 opacity
  return {
    background: l.bg,
    border: `0.5px solid ${l.border}55`,
    borderRadius: 3,
    opacity: isToday ? 1 : 0.5,
  };
}

// ── Countdown to next best window ─────────────────────────────────────────────
function Countdown({ hoursAhead, minute, second }) {
  const totalSeconds = hoursAhead * 3600 - minute * 60 - second;
  if (totalSeconds <= 0) return <span>Now!</span>;
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return (
    <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
      {h > 0 && `${h}h `}{String(m).padStart(2,'0')}m {String(s).padStart(2,'0')}s
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RunpodLoadActivity({ loadPattern }) {
  const pattern = loadPattern || DEFAULT_PATTERN;

  // Detect user timezone automatically, fall back to UTC
  const detectedTZ = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; }
  })();
  // Find detected TZ in our list, else default to UTC
  const allZones = TZ_GROUPS.flatMap(g => g.zones);
  const matchedTZ = allZones.find(z => z.value === detectedTZ)?.value || 'UTC';

  const [tz, setTz]   = useState(matchedTZ);
  const [now, setNow] = useState(() => getNow(tz));
  const [tick, setTick] = useState(0);

  // Tick every second
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(getNow(tz));
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [tz]);

  // Recompute when tz changes
  useEffect(() => { setNow(getNow(tz)); }, [tz]);

  const { dayIdx: localDay, hour: localHour, minute, second } = now;
  const { utcDay: nowUtcDay, utcHour: nowUtcHour } = localToUTC(localDay, localHour, tz);
  const currentLoad = getLoadAt(pattern, nowUtcDay, nowUtcHour);
  const currentLevel = LEVELS[currentLoad];
  const bestWindow = findNextBestWindow(pattern, localDay, localHour, tz);
  const bestLevel  = LEVELS[bestWindow.load];

  // Build local hour labels (0–23 in selected TZ)
  const hourLabels = Array.from({ length: 24 }, (_, i) => i);

  // For each local {day, hour} compute UTC and look up load
  function getLocalLoad(localDayIdx, localHourIdx) {
    const { utcDay, utcHour } = localToUTC(localDayIdx, localHourIdx, tz);
    return getLoadAt(pattern, utcDay, utcHour);
  }

  const offset = getUTCOffset(tz);

  return (
    <div style={{
      background: 'var(--sand-surface)', border: '0.5px solid var(--sand-border)',
      borderRadius: 'var(--radius-lg)', padding: '22px 24px', marginBottom: 28,
    }}>

      {/* ── Header row ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h2 style={{
            fontFamily: "'Playfair Display', serif", fontSize: 18,
            fontWeight: 600, color: 'var(--sand-text)', marginBottom: 4,
          }}>Runpod Load Activity</h2>
          <p style={{ fontSize: 12, color: 'var(--sand-muted)', fontStyle: 'italic', lineHeight: 1.5 }}>
            Based on typical GPU cloud usage patterns — best times to launch your templates.
          </p>
        </div>

        {/* Timezone selector */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 240 }}>
          <label style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
            color: 'var(--sand-hint)', letterSpacing: 1, textTransform: 'uppercase',
          }}>Your timezone</label>
          <select
            value={tz}
            onChange={e => setTz(e.target.value)}
            style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              padding: '6px 10px', border: '0.5px solid var(--sand-border)',
              borderRadius: 'var(--radius-sm)', background: 'var(--sand-bg)',
              color: 'var(--sand-text)', cursor: 'pointer', outline: 'none',
            }}
          >
            {TZ_GROUPS.map(g => (
              <optgroup key={g.label} label={g.label}>
                {g.zones.map(z => (
                  <option key={z.value} value={z.value}>{z.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)' }}>
            {offset} · {DAYS_FULL[localDay]} {String(localHour).padStart(2,'0')}:{String(minute).padStart(2,'0')}:{String(second).padStart(2,'0')}
          </span>
        </div>
      </div>

      {/* ── Status cards row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 20 }}>

        {/* Current load */}
        <div style={{
          background: currentLevel.bg, border: `1px solid ${currentLevel.border}`,
          borderRadius: 'var(--radius-md)', padding: '12px 16px',
        }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: currentLevel.text, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
            Current load
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: currentLevel.color, flexShrink: 0 }} />
            <span style={{ fontFamily: "'Source Serif 4', serif", fontSize: 16, fontWeight: 500, color: currentLevel.text }}>
              {currentLevel.label}
            </span>
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: currentLevel.text, marginTop: 4, opacity: 0.7 }}>
            {DAYS_FULL[localDay]} · {String(localHour).padStart(2,'0')}:00 local
          </div>
        </div>

        {/* Next best window */}
        <div style={{
          background: bestLevel.bg, border: `1px solid ${bestLevel.border}`,
          borderRadius: 'var(--radius-md)', padding: '12px 16px',
        }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: bestLevel.text, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
            Next quiet window
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: bestLevel.color, flexShrink: 0 }} />
            <span style={{ fontFamily: "'Source Serif 4', serif", fontSize: 16, fontWeight: 500, color: bestLevel.text }}>
              {DAYS[bestWindow.localDay]} {String(bestWindow.localHour).padStart(2,'0')}:00
            </span>
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: bestLevel.text, marginTop: 4, opacity: 0.7 }}>
            {bestWindow.hoursAhead === 0 ? 'Right now!' : <>In <Countdown hoursAhead={bestWindow.hoursAhead} minute={minute} second={second} /></>}
          </div>
        </div>

        {/* Relief tip */}
        <div style={{
          background: 'var(--sand-surface)', border: '0.5px solid var(--sand-border)',
          borderRadius: 'var(--radius-md)', padding: '12px 16px',
        }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
            Best days overall
          </div>
          <span style={{ fontFamily: "'Source Serif 4', serif", fontSize: 15, fontWeight: 500, color: 'var(--sand-text)' }}>
            Mon – Thu, 13:00–17:00 UTC
          </span>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-muted)', marginTop: 4 }}>
            Avoid daily 00:00–04:00 UTC (US night peak)
          </div>
        </div>
      </div>

      {/* ── Heatmap ── */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 580 }}>

          {/* Hour labels */}
          <div style={{ display: 'flex', marginBottom: 4, paddingLeft: 36 }}>
            {hourLabels.map(h => (
              <div key={h} style={{
                flex: 1, textAlign: 'center',
                fontFamily: "'JetBrains Mono', monospace", fontSize: 8,
                color: h === localHour ? 'var(--sand-amber-dark)' : 'var(--sand-hint)',
                fontWeight: h === localHour ? 700 : 400,
              }}>
                {h % 3 === 0 ? String(h).padStart(2,'0') : ''}
              </div>
            ))}
          </div>

          {/* Grid rows */}
          {DAYS.map((day, di) => {
            const isToday = di === localDay;
            return (
              <div key={day} style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                {/* Day label */}
                <div style={{
                  width: 32, flexShrink: 0,
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                  color: isToday ? 'var(--sand-amber-dark)' : 'var(--sand-muted)',
                  fontWeight: isToday ? 700 : 400,
                  textAlign: 'right', paddingRight: 6,
                }}>{day}</div>

                {/* Hour cells */}
                {hourLabels.map(h => {
                  const load  = getLocalLoad(di, h);
                  const isNow = isToday && h === localHour;
                  const cs    = cellStyle(load, isNow, isToday);
                  const level = LEVELS[load];
                  return (
                    <div
                      key={h}
                      title={`${DAYS_FULL[di]} ${String(h).padStart(2,'0')}:00 — ${level.label}`}
                      style={{
                        flex: 1, height: 22,
                        margin: '0 1px',
                        cursor: 'default',
                        transition: 'transform 0.1s',
                        ...cs,
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scaleY(1.3)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scaleY(1)'}
                    />
                  );
                })}
              </div>
            );
          })}

          {/* Hour labels bottom */}
          <div style={{ display: 'flex', marginTop: 4, paddingLeft: 36 }}>
            {hourLabels.map(h => (
              <div key={h} style={{
                flex: 1, textAlign: 'center',
                fontFamily: "'JetBrains Mono', monospace", fontSize: 8,
                color: 'var(--sand-hint)',
              }}>
                {h % 6 === 0 ? String(h).padStart(2,'0') : ''}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 14, alignItems: 'center' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', textTransform: 'uppercase', letterSpacing: 1 }}>Load:</span>
        {LEVELS.map((l, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: l.bg, border: `1px solid ${l.border}` }} />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: l.text }}>{l.label}</span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 8 }}>
          <div style={{ width: 12, height: 12, borderRadius: 2, background: LEVELS[currentLoad].color, border: '2px solid #333' }} />
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-muted)' }}>= Now</span>
        </div>
      </div>

      <p style={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
        color: 'var(--sand-hint)', marginTop: 12, lineHeight: 1.6,
      }}>
        Pattern reflects hobbyist/consumer GPU demand. Peak = US evening 9–11pm ET (UTC 00–04). Quiet = US business day low hobbyist use (UTC 13–17). Verified against Auckland real-world observation. Admin can override via Firestore.
      </p>
    </div>
  );
}
